"use client"

import { useState } from "react"
import { Save, User, Lock, Bell, Sliders, HelpCircle, Info, Sun } from "react-feather"
import Card from "./ui/Card"
import Button from "./ui/Button"
import "../styles/Settings.css"

function Settings() {
  const [activeTab, setActiveTab] = useState("general")
  const [notificationsEnabled, setNotificationsEnabled] = useState(true)
  const [emailAlerts, setEmailAlerts] = useState(true)
  const [smsAlerts, setSmsAlerts] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [autoExport, setAutoExport] = useState(true)
  const [exportFrequency, setExportFrequency] = useState("weekly")

  const handleSaveSettings = (e) => {
    e.preventDefault()
    // In a real app, this would save the settings to a backend
    alert("Settings saved successfully!")
  }

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          <button
            className={`settings-tab ${activeTab === "general" ? "active" : ""}`}
            onClick={() => setActiveTab("general")}
          >
            <Sliders size={18} />
            <span>General</span>
          </button>
          <button
            className={`settings-tab ${activeTab === "account" ? "active" : ""}`}
            onClick={() => setActiveTab("account")}
          >
            <User size={18} />
            <span>Account</span>
          </button>
          <button
            className={`settings-tab ${activeTab === "notifications" ? "active" : ""}`}
            onClick={() => setActiveTab("notifications")}
          >
            <Bell size={18} />
            <span>Notifications</span>
          </button>
          <button
            className={`settings-tab ${activeTab === "security" ? "active" : ""}`}
            onClick={() => setActiveTab("security")}
          >
            <Lock size={18} />
            <span>Security</span>
          </button>
          <button
            className={`settings-tab ${activeTab === "help" ? "active" : ""}`}
            onClick={() => setActiveTab("help")}
          >
            <HelpCircle size={18} />
            <span>Help & Support</span>
          </button>
          <button
            className={`settings-tab ${activeTab === "about" ? "active" : ""}`}
            onClick={() => setActiveTab("about")}
          >
            <Info size={18} />
            <span>About</span>
          </button>
        </div>

        <div className="settings-content">
          {activeTab === "general" && (
            <Card className="settings-card">
              <h2>General Settings</h2>
              <form onSubmit={handleSaveSettings}>
                <div className="settings-section">
                  <h3>Appearance</h3>
                  <div className="setting-item">
                    <div className="setting-label">
                      <label htmlFor="dark-mode">Dark Mode</label>
                      <p className="setting-description">Enable dark mode for the dashboard</p>
                    </div>
                    <div className="setting-control">
                      <label className="toggle">
                        <input
                          type="checkbox"
                          id="dark-mode"
                          checked={darkMode}
                          onChange={() => setDarkMode(!darkMode)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="settings-section">
                  <h3>Data Export</h3>
                  <div className="setting-item">
                    <div className="setting-label">
                      <label htmlFor="auto-export">Automatic Export</label>
                      <p className="setting-description">Automatically export solar data</p>
                    </div>
                    <div className="setting-control">
                      <label className="toggle">
                        <input
                          type="checkbox"
                          id="auto-export"
                          checked={autoExport}
                          onChange={() => setAutoExport(!autoExport)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>

                  {autoExport && (
                    <div className="setting-item">
                      <div className="setting-label">
                        <label htmlFor="export-frequency">Export Frequency</label>
                        <p className="setting-description">How often to export data</p>
                      </div>
                      <div className="setting-control">
                        <select
                          id="export-frequency"
                          value={exportFrequency}
                          onChange={(e) => setExportFrequency(e.target.value)}
                          className="select-input"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                <div className="settings-section">
                  <h3>System Units</h3>
                  <div className="setting-item">
                    <div className="setting-label">
                      <label htmlFor="unit-system">Unit System</label>
                      <p className="setting-description">Choose your preferred unit system</p>
                    </div>
                    <div className="setting-control">
                      <select id="unit-system" defaultValue="metric" className="select-input">
                        <option value="metric">Metric (kWh, °C)</option>
                        <option value="imperial">Imperial (kWh, °F)</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <Button variant="primary" type="submit">
                    <Save size={16} />
                    <span>Save Changes</span>
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {activeTab === "notifications" && (
            <Card className="settings-card">
              <h2>Notification Settings</h2>
              <form onSubmit={handleSaveSettings}>
                <div className="settings-section">
                  <h3>Notification Preferences</h3>
                  <div className="setting-item">
                    <div className="setting-label">
                      <label htmlFor="enable-notifications">Enable Notifications</label>
                      <p className="setting-description">Receive notifications about your solar system</p>
                    </div>
                    <div className="setting-control">
                      <label className="toggle">
                        <input
                          type="checkbox"
                          id="enable-notifications"
                          checked={notificationsEnabled}
                          onChange={() => setNotificationsEnabled(!notificationsEnabled)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>

                  {notificationsEnabled && (
                    <>
                      <div className="setting-item">
                        <div className="setting-label">
                          <label htmlFor="email-alerts">Email Alerts</label>
                          <p className="setting-description">Receive alerts via email</p>
                        </div>
                        <div className="setting-control">
                          <label className="toggle">
                            <input
                              type="checkbox"
                              id="email-alerts"
                              checked={emailAlerts}
                              onChange={() => setEmailAlerts(!emailAlerts)}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                      </div>

                      <div className="setting-item">
                        <div className="setting-label">
                          <label htmlFor="sms-alerts">SMS Alerts</label>
                          <p className="setting-description">Receive alerts via SMS</p>
                        </div>
                        <div className="setting-control">
                          <label className="toggle">
                            <input
                              type="checkbox"
                              id="sms-alerts"
                              checked={smsAlerts}
                              onChange={() => setSmsAlerts(!smsAlerts)}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                      </div>

                      <div className="setting-item">
                        <div className="setting-label">
                          <label htmlFor="alert-types">Alert Types</label>
                          <p className="setting-description">Select which types of alerts to receive</p>
                        </div>
                        <div className="setting-control checkboxes">
                          <label className="checkbox-label">
                            <input type="checkbox" defaultChecked />
                            <span>System Errors</span>
                          </label>
                          <label className="checkbox-label">
                            <input type="checkbox" defaultChecked />
                            <span>Production Anomalies</span>
                          </label>
                          <label className="checkbox-label">
                            <input type="checkbox" defaultChecked />
                            <span>Maintenance Reminders</span>
                          </label>
                          <label className="checkbox-label">
                            <input type="checkbox" defaultChecked />
                            <span>Battery Status</span>
                          </label>
                          <label className="checkbox-label">
                            <input type="checkbox" defaultChecked />
                            <span>Weather Alerts</span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="form-actions">
                  <Button variant="primary" type="submit">
                    <Save size={16} />
                    <span>Save Changes</span>
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {activeTab === "account" && (
            <Card className="settings-card">
              <h2>Account Settings</h2>
              <form onSubmit={handleSaveSettings}>
                <div className="settings-section">
                  <h3>Profile Information</h3>
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input type="text" id="name" defaultValue="John Doe" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" defaultValue="john.doe@example.com" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input type="tel" id="phone" defaultValue="+1 (555) 123-4567" />
                  </div>
                </div>

                <div className="settings-section">
                  <h3>Company Information</h3>
                  <div className="form-group">
                    <label htmlFor="company">Company Name</label>
                    <input type="text" id="company" defaultValue="Acme Solar Inc." />
                  </div>
                  <div className="form-group">
                    <label htmlFor="address">Address</label>
                    <textarea id="address" rows="3" defaultValue="123 Solar Street, Sunnyville, CA 94123"></textarea>
                  </div>
                </div>

                <div className="form-actions">
                  <Button variant="primary" type="submit">
                    <Save size={16} />
                    <span>Save Changes</span>
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {activeTab === "security" && (
            <Card className="settings-card">
              <h2>Security Settings</h2>
              <form onSubmit={handleSaveSettings}>
                <div className="settings-section">
                  <h3>Change Password</h3>
                  <div className="form-group">
                    <label htmlFor="current-password">Current Password</label>
                    <input type="password" id="current-password" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="new-password">New Password</label>
                    <input type="password" id="new-password" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="confirm-password">Confirm New Password</label>
                    <input type="password" id="confirm-password" />
                  </div>
                </div>

                <div className="settings-section">
                  <h3>Two-Factor Authentication</h3>
                  <div className="setting-item">
                    <div className="setting-label">
                      <label htmlFor="enable-2fa">Enable 2FA</label>
                      <p className="setting-description">Add an extra layer of security to your account</p>
                    </div>
                    <div className="setting-control">
                      <label className="toggle">
                        <input type="checkbox" id="enable-2fa" />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <Button variant="primary" type="submit">
                    <Save size={16} />
                    <span>Save Changes</span>
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {activeTab === "help" && (
            <Card className="settings-card">
              <h2>Help & Support</h2>
              <div className="settings-section">
                <h3>Documentation</h3>
                <p>Access our comprehensive documentation to learn more about your solar system.</p>
                <Button variant="outline" className="mt-2">
                  View Documentation
                </Button>
              </div>

              <div className="settings-section">
                <h3>Contact Support</h3>
                <p>Need help? Our support team is available 24/7 to assist you with any questions or issues.</p>
                <div className="contact-options">
                  <Button variant="outline" className="mt-2">
                    Email Support
                  </Button>
                  <Button variant="outline" className="mt-2 ml-2">
                    Live Chat
                  </Button>
                  <Button variant="outline" className="mt-2 ml-2">
                    Call Support
                  </Button>
                </div>
              </div>

              <div className="settings-section">
                <h3>Frequently Asked Questions</h3>
                <div className="faq-list">
                  <div className="faq-item">
                    <h4>How do I interpret my solar production data?</h4>
                    <p>
                      Your solar production data shows how much energy your system is generating. The dashboard provides
                      various charts and metrics to help you understand your system's performance.
                    </p>
                  </div>
                  <div className="faq-item">
                    <h4>What should I do if my system is underperforming?</h4>
                    <p>
                      If you notice a significant drop in production, check for shading issues, dirty panels, or system
                      errors. Contact support if the issue persists.
                    </p>
                  </div>
                  <div className="faq-item">
                    <h4>How often should I clean my solar panels?</h4>
                    <p>
                      We recommend cleaning your solar panels every 6-12 months, depending on your local environment and
                      weather conditions.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {activeTab === "about" && (
            <Card className="settings-card">
              <h2>About SolarDash</h2>
              <div className="settings-section">
                <div className="about-logo">
                  <div className="logo-icon-large">
                    <Sun size={48} className="sun-icon" />
                  </div>
                  <h3>SolarDash</h3>
                </div>
                <p className="about-version">Version 2.4.1</p>
                <p className="about-description">
                  SolarDash is a comprehensive solar monitoring platform that helps you track, analyze, and optimize
                  your solar energy system's performance.
                </p>
              </div>

              <div className="settings-section">
                <h3>System Information</h3>
                <div className="system-info-list">
                  <div className="info-item">
                    <div className="info-label">System Type</div>
                    <div className="info-value">Residential Solar + Battery</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Installed Capacity</div>
                    <div className="info-value">10.5 kW</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Battery Capacity</div>
                    <div className="info-value">13.5 kWh</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Installation Date</div>
                    <div className="info-value">June 15, 2022</div>
                  </div>
                  <div className="info-item">
                    <div className="info-label">Last Maintenance</div>
                    <div className="info-value">November 10, 2023</div>
                  </div>
                </div>
              </div>

              <div className="settings-section">
                <h3>Legal Information</h3>
                <div className="legal-links">
                  <a href="#" className="legal-link">
                    Terms of Service
                  </a>
                  <a href="#" className="legal-link">
                    Privacy Policy
                  </a>
                  <a href="#" className="legal-link">
                    License Information
                  </a>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

export default Settings

